import { css } from "@emotion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Router } from "../App";
import { trpcClient } from "../lib/trpc";
import { useOrganisation } from "../lib/organisation";
import { slugify } from "../lib/slugify";
import { Button } from "@/components/button";
import { Modal, ConfirmModal } from "@/components/modal";
import { PlayerGridLinkModal } from "@/components/player-grid-link-modal";
import { Future } from "@swan-io/boxed";
import type { Grid as GridType } from "@/types/grid";
import { Trash, PencilSimple, Plus, X, Trophy } from "@phosphor-icons/react";
import {
  appContainer,
  appHeader,
  appTitle,
  appSubtitle,
  pageContent,
  pageSection,
  pageSectionHeader,
  pageSectionTitle,
  pageEmpty,
  eventsList,
  eventItem,
  eventItemClickable,
  eventContent,
  eventName,
  eventDate,
  actionButtons,
  loading,
  error,
  formGroup,
  formLabel,
  formInput,
  formError,
  modalActions,
  modalDescription,
  tableContainer,
  table,
  tableHeader,
  tableHeaderCell,
  tableRow,
  tableCell,
  tableCellActions,
  paginationContainer,
  paginationInfo,
  paginationButtons,
  desktopTable,
  mobileTableCard,
  mobileCard,
  mobileCardRow,
  mobileCardLabel,
  mobileCardValue,
} from "@/styles/shared";

const gridChipList = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const gridChip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--color-bg-warm);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  color: var(--text-primary);
`;

const gridCount = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  background: var(--surface-elevated, #f3f4f6);
  color: var(--text-primary);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
`;

const playerCountSummary = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin-bottom: 12px;
`;

const gridRemoveButton = css`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    color: var(--color-danger, #e53e3e);
    background: var(--error-bg);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const gridSearchContainer = css`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const gridSearchInput = css`
  width: 100%;
  padding: 10px 14px;
  border-radius: var(--radius-md);
  border: 2px solid var(--input-border);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--text-primary);
  background: var(--input-bg);
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--input-border-focus);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const gridSearchResults = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const gridSearchResultItem = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--surface-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    border-color: var(--accent);
    background: var(--accent-subtle);
  }
`;

const pendingGridChip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--color-success-bg, #d1fae5);
  border: 1px solid var(--color-success, #10b981);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  color: var(--color-success-text, #065f46);
`;

const editButton = css`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    color: var(--text-accent);
    background: var(--color-bg-warm);
  }
`;

const sectionsLayout = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const gamesSection = css`
  min-width: 0;
`;

const playersSection = css`
  min-width: 0;
`;

const gamesList = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const gameCard = css`
  background: var(--container-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 16px;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px var(--shadow-medium);
    border-color: var(--input-border-focus);
  }
`;

const gameCardOngoing = css`
  border-left: 4px solid var(--color-primary);
`;

const gameCardHeader = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const gameCardType = css`
  font-family: var(--font-heading);
  font-weight: 700;
  color: var(--text-primary);
`;

const gameCardStatus = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

const gameCardActions = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const gameDeleteButton = css`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-muted);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: var(--color-error);
    background: rgba(231, 76, 60, 0.1);
  }

  svg {
    display: block;
  }
`;

const gameCardWinners = css`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border);
`;

const gameCardWinnersTitle = css`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-gold-dark);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const gameCardWinnersList = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const gameCardWinnerChip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, rgba(241, 196, 15, 0.15) 0%, rgba(241, 196, 15, 0.08) 100%);
  border: 1px solid var(--color-gold-dark);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: var(--color-gold-dark);
  font-weight: 500;
`;

const actionButtonsCell = css`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const PLAYERS_PER_PAGE = 10;

type Game = {
  id: string;
  type: "line" | "full-grid";
  status: "Ongoing" | "Completed";
  eventId: string;
  createdAt: string;
  updatedAt: string;
};

type GameWinner = {
  gridId: string;
  identifier: string;
  playerName: string | null;
};

interface EventPageProps {
  name: string;
  id: string;
}

export function EventPage({ name, id }: EventPageProps) {
  const { t } = useTranslation();
  const { currentOrganisation } = useOrganisation();
  const orgSlug = slugify(currentOrganisation?.name ?? "");
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [gameType, setGameType] = useState<"line" | "grid">("line");
  const [showConfirmCompleteModal, setShowConfirmCompleteModal] = useState(false);
  const [ongoingGameId, setOngoingGameId] = useState<string | null>(null);
  const [grids, setGrids] = useState<GridType[]>([]);
  const [gridsLoading, setGridsLoading] = useState(true);
  const [gridsError, setGridsError] = useState<string | null>(null);
  const [showAddGridModal, setShowAddGridModal] = useState(false);
  const [removingGridId, setRemovingGridId] = useState<string | null>(null);
  const [gridToRemove, setGridToRemove] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPlayer, setEditingPlayer] = useState<{
    playerId: string;
    phone: string;
    firstname: string;
    lastname: string;
    grids: GridType[];
  } | null>(null);

  // Grid search and pending additions for edit modal
  const [gridSearchQuery, setGridSearchQuery] = useState("");
  const [gridSearchResults, setGridSearchResults] = useState<GridType[]>([]);

  // Winners per game
  const [gameWinners, setGameWinners] = useState<Record<string, GameWinner[]>>({});
  const [gameToDelete, setGameToDelete] = useState<Game | null>(null);
  const [deletingGame, setDeletingGame] = useState(false);
  const [gridSearchLoading, setGridSearchLoading] = useState(false);
  const [pendingGrids, setPendingGrids] = useState<GridType[]>([]);
  const [savingGrids, setSavingGrids] = useState(false);

  const fetchGames = async () => {
    setIsLoading(true);
    await Future.fromPromise(trpcClient.listGames.query({ eventId: id, limit: 50, offset: 0 }))
      .mapOk(setGames)
      .mapOk(() => setErr(null))
      .mapOk(() => setIsLoading(false))
      .mapError((e) => {
        setErr(e instanceof Error ? e.message : "Failed to load games");
        console.error("Error fetching games:", e);
      })
      .toPromise();
  };

  const fetchGridsToPlayAutomatically = async () => {
    setGridsLoading(true);
    await Future.fromPromise(trpcClient.listGridsToPlayAutomatically.query({ eventId: id }))
      .mapOk((result) =>
        result.map(
          (grid: any): GridType => ({
            id: grid.id,
            fileUrl: grid.fileUrl,
            identifier: grid.identifier ?? undefined,
            numbers: grid.numbers ?? null,
            status: grid.status,
            playerInfo: grid.playerInfo,
          }),
        ),
      )
      .mapOk(setGrids)
      .mapOk(() => setGridsError(null))
      .mapOk(() => setGridsLoading(false))
      .mapError((e) => {
        setGridsError(
          e instanceof Error ? e.message : "Failed to load grids to play automatically",
        );
        console.error("Error fetching grids to play automatically:", e);
      })
      .toPromise();
  };

  useEffect(() => {
    fetchGames();
    fetchGridsToPlayAutomatically();
  }, [id]);

  // Fetch winners for each game
  useEffect(() => {
    if (games.length === 0) return;

    const fetchWinnersForGames = async () => {
      const winners: Record<string, GameWinner[]> = {};
      await Promise.all(
        games.map(async (game) => {
          try {
            const grids = await trpcClient.getWinningGrids.query({ gameId: game.id });
            winners[game.id] = grids.map((g) => ({
              gridId: g.gridId,
              identifier: g.identifier,
              playerName: g.playerInfo
                ? `${g.playerInfo.firstname} ${g.playerInfo.lastname}`
                : null,
            }));
          } catch {
            winners[game.id] = [];
          }
        }),
      );
      setGameWinners(winners);
    };

    fetchWinnersForGames();
  }, [games]);

  // Reset pagination when grids change
  useEffect(() => {
    setCurrentPage(1);
  }, [grids.length]);

  const handleCreateGame = async (e: React.FormEvent) => {
    e.preventDefault();

    const ongoingGame = games.find((g) => g.status === "Ongoing");
    if (ongoingGame) {
      setOngoingGameId(ongoingGame.id);
      setShowCreateModal(false);
      setShowConfirmCompleteModal(true);
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await trpcClient.startGame.mutate({ gameType, eventId: id });
      setShowCreateModal(false);
      setGameType("line");
      await fetchGames();
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : "Failed to create game");
      console.error("Error creating game:", e);
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmCompleteAndCreate = async () => {
    if (!ongoingGameId) return;

    setCreating(true);
    setCreateError(null);

    try {
      await trpcClient.completeGame.mutate({ gameId: ongoingGameId });
      await trpcClient.startGame.mutate({ gameType, eventId: id });
      setShowConfirmCompleteModal(false);
      setOngoingGameId(null);
      setGameType("line");
      await fetchGames();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Failed to complete and create game");
      console.error("Error completing and creating game:", e);
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveGrid = async (gridId: string) => {
    setRemovingGridId(gridId);
    try {
      await trpcClient.removeGridFromAutoPlay.mutate({ gridId, eventId: id });
      await fetchGridsToPlayAutomatically();
    } catch (e) {
      console.error("Error removing grid from auto play:", e);
    } finally {
      setRemovingGridId(null);
      setGridToRemove(null);
    }
  };

  const searchGridsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleGridSearch = useCallback(
    (query: string) => {
      setGridSearchQuery(query);

      if (searchGridsDebounceRef.current) {
        clearTimeout(searchGridsDebounceRef.current);
      }

      if (query.length < 2) {
        setGridSearchResults([]);
        return;
      }

      searchGridsDebounceRef.current = setTimeout(async () => {
        setGridSearchLoading(true);
        try {
          const results = await trpcClient.searchGridsByIdentifier.query({
            identifier: query,
          });
          // Filter out grids already linked to this player
          const existingGridIds = new Set(editingPlayer?.grids.map((g) => g.id) ?? []);
          const pendingGridIds = new Set(pendingGrids.map((g) => g.id));
          const filteredResults = results.filter(
            (g: GridType) => !existingGridIds.has(g.id) && !pendingGridIds.has(g.id),
          );
          setGridSearchResults(filteredResults);
        } catch (e) {
          console.error("Error searching grids:", e);
          setGridSearchResults([]);
        } finally {
          setGridSearchLoading(false);
        }
      }, 300);
    },
    [editingPlayer?.grids, pendingGrids],
  );

  const handleAddPendingGrid = (grid: GridType) => {
    setPendingGrids((prev) => [...prev, grid]);
    setGridSearchResults((prev) => prev.filter((g) => g.id !== grid.id));
    setGridSearchQuery("");
  };

  const handleRemovePendingGrid = (gridId: string) => {
    setPendingGrids((prev) => prev.filter((g) => g.id !== gridId));
  };

  const handleSavePendingGrids = async () => {
    if (!editingPlayer || pendingGrids.length === 0) return;

    setSavingGrids(true);
    try {
      for (const grid of pendingGrids) {
        await trpcClient.playGridAutomatically.mutate({
          gridId: grid.id,
          eventId: id,
          firstname: editingPlayer.firstname,
          lastname: editingPlayer.lastname,
          phone: editingPlayer.phone,
        });
      }
      await fetchGridsToPlayAutomatically();
      setPendingGrids([]);
      setEditingPlayer(null);
    } catch (e) {
      console.error("Error saving grids:", e);
    } finally {
      setSavingGrids(false);
    }
  };

  const handlePlayerSelectedFromSearch = (player: {
    id: string;
    firstname: string;
    lastname: string;
    phone: string;
  }) => {
    // Find existing grids for this player in current event
    const playerGrids = grids.filter(
      (g) => g.playerInfo?.playerId === player.id,
    );

    setShowAddGridModal(false);
    setEditingPlayer({
      playerId: player.id,
      phone: player.phone,
      firstname: player.firstname,
      lastname: player.lastname,
      grids: playerGrids,
    });
  };

  const handleCancelEdit = () => {
    setPendingGrids([]);
    setGridSearchQuery("");
    setGridSearchResults([]);
    setEditingPlayer(null);
  };

  const handleDeleteGame = async () => {
    if (!gameToDelete) return;

    setDeletingGame(true);
    try {
      await trpcClient.deleteGame.mutate({ gameId: gameToDelete.id });
      await fetchGames();
      setGameToDelete(null);
    } catch (e) {
      console.error("Error deleting game:", e);
    } finally {
      setDeletingGame(false);
    }
  };

  return (
    <div css={appContainer}>
      <header css={appHeader}>
        <div>
          <h1 css={appTitle}>{decodeURIComponent(name)}</h1>
          <p css={appSubtitle}>{t("eventDetail.subtitle")}</p>
        </div>
        <div css={actionButtons}>
          <Button onClick={() => setShowCreateModal(true)} variant="primary" size="sm">
            {t("eventDetail.startGame")}
          </Button>
        </div>
      </header>

      <main css={pageContent}>
        <div css={sectionsLayout}>
          {/* Games section - at the top */}
          <section css={[pageSection, gamesSection]}>
            <h3 css={pageSectionTitle}>{t("eventDetail.gamesTitle")}</h3>
            {isLoading && <p css={loading}>{t("eventDetail.loadingGames")}</p>}
            {err && <p css={error}>{t("common.error", { message: err })}</p>}
            {!isLoading && !err && games.length === 0 && (
              <p css={pageEmpty}>{t("eventDetail.noGames")}</p>
            )}
            {!isLoading && !err && games.length > 0 && (
              <div css={gamesList}>
                {games.map((game) => {
                  const winners = gameWinners[game.id] ?? [];
                  return (
                    <div
                      key={game.id}
                      css={[gameCard, game.status === "Ongoing" && gameCardOngoing]}
                      onClick={() => Router.push("Game", { orgSlug, id: game.id })}
                    >
                      <div css={gameCardHeader}>
                        <span css={gameCardType}>{t(`gameType.${game.type}`)}</span>
                        <div css={gameCardActions}>
                          <span css={gameCardStatus}>{t(`gameStatus.${game.status}`)}</span>
                          <button
                            type="button"
                            css={gameDeleteButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              setGameToDelete(game);
                            }}
                            title={t("eventDetail.deleteGame")}
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                      {winners.length > 0 && (
                        <div css={gameCardWinners}>
                          <div css={gameCardWinnersTitle}>
                            <Trophy size={14} weight="fill" />
                            {t("eventDetail.winnersLabel", { count: winners.length })}
                          </div>
                          <div css={gameCardWinnersList}>
                            {winners.map((w) => (
                              <span key={w.gridId} css={gameCardWinnerChip}>
                                {w.identifier}
                                {w.playerName && ` - ${w.playerName}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Players section - below games */}
          <section css={[pageSection, playersSection]}>
            <div css={pageSectionHeader}>
              <h3 css={pageSectionTitle}>{t("eventDetail.gridsTitle")}</h3>
              <div css={actionButtons}>
                <Button
                  onClick={() => fetchGridsToPlayAutomatically()}
                  variant="secondary"
                  size="sm"
                  disabled={gridsLoading}
                >
                  {gridsLoading ? t("common.refreshing") : t("common.refresh")}
                </Button>
                <Button onClick={() => setShowAddGridModal(true)} variant="primary" size="sm">
                  <Plus size={16} weight="bold" />
                  {t("eventDetail.addGrid")}
                </Button>
              </div>
            </div>
            {gridsLoading && <p css={loading}>{t("eventDetail.loadingGrids")}</p>}
            {gridsError && <p css={error}>{t("common.error", { message: gridsError })}</p>}

            {!gridsLoading && !gridsError && grids.length === 0 && (
              <p css={pageEmpty}>{t("eventDetail.noGrids")}</p>
            )}

            {!gridsLoading &&
              !gridsError &&
              grids.length > 0 &&
              (() => {
                const players = Object.values(
                  grids.reduce<
                    Record<
                      string,
                      { playerId: string; firstname: string; lastname: string; phone: string; grids: GridType[] }
                    >
                  >((acc, grid) => {
                    const key = grid.playerInfo?.playerId ?? grid.id;
                    if (!acc[key]) {
                      acc[key] = {
                        playerId: grid.playerInfo?.playerId ?? "",
                        firstname: grid.playerInfo?.firstname ?? "",
                        lastname: grid.playerInfo?.lastname ?? "",
                        phone: grid.playerInfo?.phone ?? "",
                        grids: [],
                      };
                    }
                    acc[key].grids.push(grid);
                    return acc;
                  }, {}),
                );

                const totalPages = Math.ceil(players.length / PLAYERS_PER_PAGE);
                const startIndex = (currentPage - 1) * PLAYERS_PER_PAGE;
                const paginatedPlayers = players.slice(startIndex, startIndex + PLAYERS_PER_PAGE);
                const totalGrids = players.reduce((sum, player) => sum + player.grids.length, 0);

                return (
                  <>
                    {/* Player count summary */}
                    <p css={playerCountSummary}>
                      {t("eventDetail.playerCount", { count: players.length })} —{" "}
                      {t("eventDetail.gridCount", { count: totalGrids })}
                    </p>

                    {/* Desktop table */}
                    <div css={desktopTable}>
                      <div css={tableContainer}>
                        <table css={table}>
                          <thead css={tableHeader}>
                            <tr>
                              <th css={tableHeaderCell}>{t("eventDetail.tableHeaderFirstname")}</th>
                              <th css={tableHeaderCell}>{t("eventDetail.tableHeaderLastname")}</th>
                              <th css={tableHeaderCell}>{t("eventDetail.tableHeaderPhone")}</th>
                              <th css={tableHeaderCell}>{t("eventDetail.tableHeaderGrids")}</th>
                              <th css={tableHeaderCell}>{t("eventDetail.tableHeaderActions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedPlayers.map((player) => (
                              <tr key={player.playerId || player.grids[0]?.id} css={tableRow}>
                                <td css={tableCell}>{player.firstname}</td>
                                <td css={tableCell}>{player.lastname}</td>
                                <td css={tableCell}>{player.phone || "—"}</td>
                                <td css={tableCell}>
                                  <span css={gridCount}>{player.grids.length}</span>
                                </td>
                                <td css={tableCellActions}>
                                  <div css={actionButtonsCell}>
                                    <button
                                      css={editButton}
                                      onClick={() => setEditingPlayer(player)}
                                      title={t("eventDetail.editPlayerGrids")}
                                    >
                                      <PencilSimple size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile cards */}
                    <div css={mobileTableCard}>
                      {paginatedPlayers.map((player) => (
                        <div key={player.playerId || player.grids[0]?.id} css={mobileCard}>
                          <div css={mobileCardRow}>
                            <span css={mobileCardLabel}>
                              {t("eventDetail.tableHeaderFirstname")}
                            </span>
                            <span css={mobileCardValue}>{player.firstname}</span>
                          </div>
                          <div css={mobileCardRow}>
                            <span css={mobileCardLabel}>
                              {t("eventDetail.tableHeaderLastname")}
                            </span>
                            <span css={mobileCardValue}>{player.lastname}</span>
                          </div>
                          <div css={mobileCardRow}>
                            <span css={mobileCardLabel}>{t("eventDetail.tableHeaderPhone")}</span>
                            <span css={mobileCardValue}>{player.phone || "—"}</span>
                          </div>
                          <div css={mobileCardRow}>
                            <span css={mobileCardLabel}>{t("eventDetail.tableHeaderGrids")}</span>
                            <span css={mobileCardValue}>
                              <span css={gridCount}>{player.grids.length}</span>
                            </span>
                          </div>
                          <div css={mobileCardRow}>
                            <button
                              css={editButton}
                              onClick={() => setEditingPlayer(player)}
                              title={t("eventDetail.editPlayerGrids")}
                            >
                              <PencilSimple size={16} />
                              {t("common.edit")}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div css={paginationContainer}>
                        <span css={paginationInfo}>
                          {t("eventDetail.pageOf", { current: currentPage, total: totalPages })}
                        </span>
                        <div css={paginationButtons}>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            {t("eventDetail.previous")}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            {t("eventDetail.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
          </section>
        </div>
      </main>

      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={t("eventDetail.startNewGame")}
      >
        <form onSubmit={handleCreateGame}>
          <div css={formGroup}>
            <label htmlFor="game-type" css={formLabel}>
              {t("eventDetail.gameTypeLabel")}
            </label>
            <select
              id="game-type"
              value={gameType}
              onChange={(e) => setGameType(e.target.value as "line" | "grid")}
              css={formInput}
              required
              disabled={creating}
            >
              <option value="line">{t("gameType.line")}</option>
              <option value="grid">{t("gameType.grid")}</option>
            </select>
          </div>
          {createError && <div css={formError}>{createError}</div>}
          <div css={modalActions}>
            <Button
              onClick={() => {
                setShowCreateModal(false);
                setGameType("line");
                setCreateError(null);
              }}
              variant="secondary"
              disabled={creating}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="primary" disabled={creating}>
              {creating ? t("eventDetail.starting") : t("eventDetail.startGame")}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={showConfirmCompleteModal}
        onClose={() => {
          setShowConfirmCompleteModal(false);
          setOngoingGameId(null);
        }}
        title={t("eventDetail.ongoingGameExists")}
      >
        <p css={modalDescription}>{t("eventDetail.ongoingGameMessage")}</p>
        {createError && <div css={formError}>{createError}</div>}
        <div css={modalActions}>
          <Button
            onClick={() => {
              setShowConfirmCompleteModal(false);
              setOngoingGameId(null);
              setCreateError(null);
            }}
            variant="secondary"
            disabled={creating}
          >
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirmCompleteAndCreate} variant="primary" disabled={creating}>
            {creating ? t("eventDetail.processing") : t("eventDetail.completeAndCreate")}
          </Button>
        </div>
      </Modal>

      <PlayerGridLinkModal
        open={showAddGridModal}
        onClose={() => setShowAddGridModal(false)}
        eventId={id}
        onSuccess={() => fetchGridsToPlayAutomatically()}
        onPlayerSelected={handlePlayerSelectedFromSearch}
      />

      <ConfirmModal
        open={gridToRemove !== null}
        onClose={() => setGridToRemove(null)}
        onConfirm={() => gridToRemove && handleRemoveGrid(gridToRemove)}
        title={t("common.confirm")}
        message={t("eventDetail.confirmRemoveGrid")}
        variant="danger"
        loading={removingGridId !== null}
      />

      <ConfirmModal
        open={gameToDelete !== null}
        onClose={() => setGameToDelete(null)}
        onConfirm={handleDeleteGame}
        title={t("eventDetail.deleteGameTitle")}
        message={
          gameToDelete?.status === "Completed"
            ? t("eventDetail.deleteCompletedGameWarning")
            : t("eventDetail.deleteGameMessage")
        }
        variant="danger"
        loading={deletingGame}
      />

      {/* Edit player grids modal */}
      <Modal
        open={editingPlayer !== null}
        onClose={handleCancelEdit}
        title={t("eventDetail.editPlayerGridsTitle", {
          name: editingPlayer ? `${editingPlayer.firstname} ${editingPlayer.lastname}` : "",
        })}
      >
        {editingPlayer && (
          <>
            <p css={modalDescription}>{t("eventDetail.editPlayerGridsDescription")}</p>

            {/* Existing grids */}
            <div css={gridChipList}>
              {editingPlayer.grids.map((grid) => (
                <span key={grid.id} css={gridChip}>
                  {grid.identifier ?? t("eventDetail.noIdentifier")}
                  <button
                    css={gridRemoveButton}
                    onClick={async () => {
                      await handleRemoveGrid(grid.id);
                      setEditingPlayer((prev) => {
                        if (!prev) return null;
                        const updatedGrids = prev.grids.filter((g) => g.id !== grid.id);
                        if (updatedGrids.length === 0) return null;
                        return { ...prev, grids: updatedGrids };
                      });
                    }}
                    disabled={removingGridId === grid.id || savingGrids}
                    title={t("eventDetail.removeGrid")}
                  >
                    <Trash size={14} />
                  </button>
                </span>
              ))}

              {/* Pending grids (to be added) */}
              {pendingGrids.map((grid) => (
                <span key={grid.id} css={pendingGridChip}>
                  {grid.identifier ?? t("eventDetail.noIdentifier")}
                  <button
                    css={gridRemoveButton}
                    onClick={() => handleRemovePendingGrid(grid.id)}
                    disabled={savingGrids}
                    title={t("common.cancel")}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>

            {/* Grid search */}
            <div css={gridSearchContainer}>
              <input
                type="text"
                css={gridSearchInput}
                placeholder={t("eventDetail.searchGridPlaceholder")}
                value={gridSearchQuery}
                onChange={(e) => handleGridSearch(e.target.value)}
                disabled={savingGrids}
              />

              {gridSearchLoading && (
                <p css={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                  {t("common.searching")}
                </p>
              )}

              {!gridSearchLoading && gridSearchResults.length > 0 && (
                <div css={gridSearchResults}>
                  {gridSearchResults.map((grid) => (
                    <div
                      key={grid.id}
                      css={gridSearchResultItem}
                      onClick={() => handleAddPendingGrid(grid)}
                    >
                      <span>{grid.identifier ?? t("eventDetail.noIdentifier")}</span>
                      <Plus size={16} />
                    </div>
                  ))}
                </div>
              )}

              {!gridSearchLoading &&
                gridSearchQuery.length >= 2 &&
                gridSearchResults.length === 0 && (
                  <p css={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                    {t("eventDetail.noGridsFoundSearch")}
                  </p>
                )}
            </div>

            <div css={modalActions}>
              {pendingGrids.length > 0 ? (
                <>
                  <Button onClick={handleCancelEdit} variant="secondary" disabled={savingGrids}>
                    {t("common.cancel")}
                  </Button>
                  <Button onClick={handleSavePendingGrids} variant="primary" disabled={savingGrids}>
                    {savingGrids ? t("common.saving") : t("common.save")}
                  </Button>
                </>
              ) : (
                <Button onClick={handleCancelEdit} variant="secondary">
                  {t("common.close")}
                </Button>
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
